import { AppBar } from './AppBar'
import { useTranslation } from '../i18n/I18nProvider'
import { useNavigation, type RouteId } from '../router'

/**
 * Rendered for any route this shell does not yet have a screen registered for. P7/P9/P15
 * replace this per route as their screens land; the router and app frame do not change.
 *
 * R1-10 (`R1-client-resilience.md`): this used to render the raw route id as both the app-bar
 * title and the body text — the one place in the running app with untranslated English and a
 * raw developer key on screen, reachable by deep-linking straight to any of the seven ids the
 * switch in `App.tsx` still falls through to here for. It also had no back affordance: a
 * hamburger with no Home row underneath it.
 *
 * The fix is the same shape every other shell/screen bar uses (`AboutScreen`, `PersonalizeScreen`
 * …): this component supplies its **own** `AppBar`, with `onBack` wired to `useNavigation().back()`
 * — which resolves to session history when there is one, and to Home when there is not (a cold
 * deep link straight into an unbuilt route), so the affordance always actually leaves. `routeId`
 * is kept as a `data-route-id` attribute rather than rendered text: useful for a test or for
 * support to ask "which one", never shown to a resident and never a translation key. Copy is
 * intentionally the same generic Hebrew string for all seven ids — this is not a screen for any
 * one of them, and inventing seven per-route strings would read as building the screen this
 * component exists to avoid building.
 */
export function RoutePlaceholder({ routeId }: { readonly routeId: RouteId }): JSX.Element {
  const { t } = useTranslation('common')
  const { back } = useNavigation()
  return (
    <>
      <AppBar title={t('placeholderTitle')} onBack={back} />
      <div
        data-route-id={routeId}
        style={{
          padding: '48px 16px',
          textAlign: 'center',
          color: 'var(--muted)',
          fontSize: 'var(--font-cardTitle)',
        }}
      >
        {t('placeholderBody')}
      </div>
    </>
  )
}
